// The verdict pack's on-demand evidence drawer (docs/phase-0/slice5-ux-flows.md §4.3;
// docs/phase-0/slice5-component-designs.md §3.1 - proposed `EvidenceDrawer`). Composed
// from `.well.pad` inside a `.card` (never `.banner` - this is detail-on-demand, not a
// dismissable disclosure). Non-modal on desktop (docs/phase-0/slice5-ux-flows.md §4.7:
// "no hard Tab-trap since it does not block the page" - true at >=1024px, where the
// drawer is an inline/sticky rail, not an overlay) - focus moves to the drawer's own
// heading on open and returns to the toggle that opened it on close; Escape closes it as
// a convenience. Below 1024px it DOES block the page (see the W4 note directly below),
// so the trap there is a requirement, not optional.
//
// FOCUS CONTAINMENT (design-review W4): at <1024px `.evidence-drawer` becomes an
// opaque, `position: fixed; inset: 0` full-screen overlay (app.css:1513-1523), so a
// keyboard user must not be able to Tab to content behind it - the desktop >=1024px
// variant (app.css:1496-1512) stays a non-modal, inline/sticky panel with no trap, per
// the ux spec's own "no hard Tab-trap since it does not block the page" (only true at
// that width). The trap only engages while the `(max-width: 1023.98px)` media query
// matches (the exact breakpoint app.css already uses for the full-screen state), mirrors
// the Tab/Shift+Tab cycle `ValidateConfirmDialog.tsx`/`BuildConfirmDialog.tsx` already
// ship, and `aria-modal` is set only in that same state.
//
// RENDERS THE CLAIM'S OWN EVIDENCE INLINE, no separate fetch (a deliberate, grounded
// choice, flagged): `VerdictClaim` already carries its full `evidence: EvidencePointer[]`
// array (model/validate.ts:51-62) as part of the verdict pack response, and the claim
// itself carries NO stable `id` field to key the landed
// `GET /v1/apps/:appId/validate/evidence/:claimId` route against (a named contract gap -
// no canon doc mints a claim id). Re-fetching data the pack already returned, against an
// endpoint with no real id to call it with, would mean fabricating a synthetic id for no
// benefit; this component instead renders the already-grounded evidence it was handed.
// The `GET evidence/:claimId` client (`src/api/validate.ts`'s `getEvidence`) stays wired
// for whichever lane resolves the id gap and wants a true on-demand per-claim fetch.
//
// NAMED CONTRACT GAP (flagged, not silently resolved): the component-design spec's sketch
// (§3.1) also shows a "What we checked" methodology line and a reused "Assuming" block for
// a modeled estimate's assumptions - neither has a backing field anywhere on the wire
// (`EvidencePointer` is only `kind`/`ref`/`checkedAt`, model/certify.ts:61-66). This
// component renders ONLY what the contract actually carries (the source list) rather than
// inventing placeholder prose for a field that does not exist (`context-discipline`:
// never invent a figure or a claim).
import { useEffect, useId, useRef, useState } from "react";
import type { EvidencePointer } from "@nightcrew/shared";
import { validateCopy } from "../copy/validate.js";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

// The exact breakpoint app.css:1513 uses for the full-screen overlay state - keep the
// two in sync (the trap must engage exactly when the overlay is opaque and fixed).
const MOBILE_FULLSCREEN_QUERY = "(max-width: 1023.98px)";

export const EvidenceDrawer = ({
  claimStatement,
  evidence,
  onClose,
  triggerRef,
}: {
  /** The claim's own statement, used as the drawer's heading when opened per-claim
   * (ux spec §4.3: "or the single claim's own heading if opened per-claim"). */
  readonly claimStatement: string;
  readonly evidence: readonly EvidencePointer[];
  readonly onClose: () => void;
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
}): React.JSX.Element => {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const [isMobileFullScreen, setIsMobileFullScreen] = useState(
    () => window.matchMedia(MOBILE_FULLSCREEN_QUERY).matches,
  );

  useEffect(() => {
    headingRef.current?.focus();
    return () => {
      triggerRef?.current?.focus();
    };
    // triggerRef is a stable ref object - omitted from deps deliberately (same pattern
    // as GdprRoutingPanel.tsx).
  }, [triggerRef]);

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_FULLSCREEN_QUERY);
    const onChange = (): void => {
      setIsMobileFullScreen(mql.matches);
    };
    mql.addEventListener("change", onChange);
    return () => {
      mql.removeEventListener("change", onChange);
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    // The Tab trap only engages for the mobile full-screen overlay - the desktop
    // inline/sticky variant is not modal and must stay freely Tab-through-able.
    if (!isMobileFullScreen || event.key !== "Tab" || panelRef.current === null) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="evidence-drawer"
      role="region"
      aria-labelledby={headingId}
      aria-modal={isMobileFullScreen ? "true" : undefined}
      ref={panelRef}
      onKeyDown={handleKeyDown}
      data-testid="evidence-drawer"
    >
      <div className="well pad card">
        <div className="row between aic gap8">
          <h2 className="h3" id={headingId} tabIndex={-1} ref={headingRef}>
            {claimStatement}
          </h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label={validateCopy("validate.evidence.closeCta")}
          >
            {validateCopy("validate.evidence.closeCta")}
          </button>
        </div>

        <p className="muted mt12" style={{ fontSize: 12, fontWeight: 600 }}>
          {validateCopy("validate.evidence.methodology")}
        </p>
        {evidence.length === 0 ? (
          <p className="muted mt8" style={{ fontSize: 13 }}>
            No sources are attached to this claim.
          </p>
        ) : (
          <ul
            className="stack gap8 mt8"
            style={{ margin: 0, padding: 0, listStyle: "none" }}
            aria-label="Sources checked"
          >
            {evidence.map((pointer, index) => (
              <li key={`${pointer.ref}-${String(index)}`} className="chip">
                {pointer.ref} · {new Date(pointer.checkedAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
